import { useEffect, useState, useRef, useCallback } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import './TutorialTooltip.css';

type Position = 'top' | 'bottom' | 'left' | 'right' | 'center';

interface TutorialTooltipProps {
  target?: string;  // CSS selector
  position?: Position;
  title?: string;
  content: string;
  copyText?: string;
  onUsePrompt?: (text: string) => void;
  showNextButton?: boolean;
  showPrevButton?: boolean;
  showSkipButton?: boolean;
  stepNumber?: number;
  totalSteps?: number;
  onNext?: () => void;
  onPrev?: () => void;
  onSkip?: () => void;
  onExit?: () => void;
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowPosition: Position;
}

export function TutorialTooltip({
  target,
  position = 'bottom',
  title,
  content,
  copyText,
  onUsePrompt,
  showNextButton = true,
  showPrevButton = false,
  showSkipButton = false,
  stepNumber,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onExit,
}: TutorialTooltipProps) {
  const isMobile = useIsMobile();
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const observedElementRef = useRef<Element | null>(null);
  const [used, setUsed] = useState(false);

  const handleUse = useCallback(() => {
    if (!copyText || !onUsePrompt) return;
    onUsePrompt(copyText);
    setUsed(true);
    setTimeout(() => setUsed(false), 2000);
  }, [copyText, onUsePrompt]);
  
  useEffect(() => {
    let lastPos: TooltipPosition | null = null;

    const commit = (next: TooltipPosition) => {
      if (
        lastPos &&
        lastPos.top === next.top &&
        lastPos.left === next.left &&
        lastPos.arrowPosition === next.arrowPosition
      ) {
        return; // no change — skip re-render
      }
      lastPos = next;
      setTooltipPos(next);
    };

    const updatePosition = () => {
      const tooltip = tooltipRef.current;
      if (!tooltip) return;

      const tooltipRect = tooltip.getBoundingClientRect();

      // Center position (no target)
      if (!target || position === 'center') {
        commit({
          top: window.innerHeight / 2 - tooltipRect.height / 2,
          left: window.innerWidth / 2 - tooltipRect.width / 2,
          arrowPosition: 'center',
        });
        return;
      }

      const element = document.querySelector(target);
      if (element !== observedElementRef.current) {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        observedElementRef.current = element;
        if (element && 'ResizeObserver' in window) {
          resizeObserverRef.current = new ResizeObserver(updatePosition);
          resizeObserverRef.current.observe(element);
        }
      }
      if (!element) {
        // Fallback to center if target not found
        commit({
          top: window.innerHeight / 2 - tooltipRect.height / 2,
          left: window.innerWidth / 2 - tooltipRect.width / 2,
          arrowPosition: 'center',
        });
        return;
      }

      const targetRect = element.getBoundingClientRect();
      const padding = 16;
      let top = 0;
      let left = 0;
      let arrowPos = position;

      switch (position) {
        case 'top':
          top = targetRect.top - tooltipRect.height - padding;
          left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
          break;
        case 'bottom':
          top = targetRect.bottom + padding;
          left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
          break;
        case 'left':
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          left = targetRect.left - tooltipRect.width - padding;
          break;
        case 'right':
          top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
          left = targetRect.right + padding;
          break;
      }

      // Keep tooltip in viewport (larger margins on mobile for avatar + safe area)
      const margin = isMobile ? 16 : 20;
      const bottomMargin = isMobile ? 60 : 20;
      if (left < margin) left = margin;
      if (left + tooltipRect.width > window.innerWidth - margin) {
        left = window.innerWidth - tooltipRect.width - margin;
      }
      if (top < margin) top = margin;
      if (top + tooltipRect.height > window.innerHeight - bottomMargin) {
        top = window.innerHeight - tooltipRect.height - bottomMargin;
      }

      commit({ top, left, arrowPosition: arrowPos });
    };
    
    // rAF polling: ReactFlow pans/zooms via CSS transforms and doesn't fire
    // window scroll events. Polling getBoundingClientRect every frame keeps
    // the tooltip glued to its target the same way the Spotlight does. Cheap
    // (one DOM read + one branch per frame) and matches the spotlight cadence.
    let rafId: number | null = null;
    const tick = () => {
      updatePosition();
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      observedElementRef.current = null;
    };
  }, [target, position, isMobile]);
  
  return (
    <div
      ref={tooltipRef}
      className={`tutorial-tooltip tutorial-tooltip--${tooltipPos?.arrowPosition || 'center'}`}
      style={{
        top: tooltipPos?.top ?? '50%',
        left: tooltipPos?.left ?? '50%',
        visibility: tooltipPos ? 'visible' : 'hidden',
      }}
    >
      {/* Iris avatar — floats outside the bubble so it looks like she's speaking */}
      <div className="tutorial-iris-avatar">
        <img src="/icons/iris-butterfly.svg" alt="Iris" />
      </div>

      {/* Exit button */}
      <button className="tutorial-tooltip-exit" onClick={onExit} title="Exit tutorial">
        ✕
      </button>
      
      {/* Step indicator */}
      {stepNumber !== undefined && totalSteps !== undefined && (
        <div className="tutorial-tooltip-step">
          Step {stepNumber} of {totalSteps}
        </div>
      )}
      
      {/* Title */}
      {title && <h3 className="tutorial-tooltip-title">{title}</h3>}
      
      {/* Content */}
      <p className="tutorial-tooltip-content">{content}</p>

      {/* Copy prompt block */}
      {copyText && (
        <div className="tutorial-tooltip-copy">
          <span className="tutorial-tooltip-copy-text">&ldquo;{copyText}&rdquo;</span>
          <button className="tutorial-tooltip-copy-btn" onClick={handleUse}>
            {used ? '✓ Used!' : 'Use'}
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="tutorial-tooltip-actions">
        {showSkipButton && (
          <button className="tutorial-tooltip-btn tutorial-tooltip-btn--skip" onClick={onSkip}>
            Skip
          </button>
        )}
        
        <div className="tutorial-tooltip-actions-right">
          {showPrevButton && (
            <button className="tutorial-tooltip-btn tutorial-tooltip-btn--prev" onClick={onPrev}>
              ← Back
            </button>
          )}
          
          {showNextButton && (
            <button className="tutorial-tooltip-btn tutorial-tooltip-btn--next" onClick={onNext}>
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
